import { CornerDownLeft, Search, X } from "lucide-react";
import { useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { Link, useNavigate } from "react-router-dom";
import { cx } from "@/components/ui";
import { sinTildes } from "@/lib/sinTildes";
import { useAuth } from "@/stores/auth";
import type { Enlace, Grupo } from "./navegacion";

/**
 * El buscador de pantallas, arriba del menú lateral.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 *  ⚠️ PARA QUIEN SE PIERDE EN EL MENÚ
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * El dueño vio a cuatro dueños perderse en la barra lateral. Son más de
 * treinta pantallas en cinco secciones, y quien busca «los proveedores» no
 * sabe que viven dentro de Inventario. Aquí se escribe lo que se busca y se
 * llega, sin conocer el mapa.
 *
 * Busca en las MISMAS pantallas que enseña el menú (con sus permisos y sus
 * interruptores ya aplicados), incluidas las que cuelgan de una sección, y
 * también por otras palabras con que la gente las llama (`claves`): «tasa» lleva
 * a Monedas y «empleados» a Equipo y a Nómina.
 *
 * Mientras hay algo escrito, los resultados ocupan el sitio del menú; al
 * borrarlo vuelve el menú tal cual estaba.
 */
export function BuscadorDelMenu({
  grupos,
  sueltos,
  enfocar,
  oculto = false,
  claseCasilla = "px-3 pb-3",
  claseLista,
  children,
}: {
  grupos: Grupo[];
  /** Lo que va fuera de los grupos (Vender, Inicio), si se ve. */
  sueltos: Enlace[];
  /** Cada vez que cambia (y no es 0), el cursor salta a la casilla. */
  enfocar: number;
  /** Con la barra recogida no hay casilla: solo la lista (la lupa la pone la barra). */
  oculto?: boolean;
  claseCasilla?: string;
  /** Las clases de la lista que se desliza, que cambian entre escritorio y celular. */
  claseLista: string;
  /** El menú de siempre, que se ve cuando no hay nada escrito. */
  children: ReactNode;
}) {
  const { puede } = useAuth();
  const navegar = useNavigate();
  const casilla = useRef<HTMLInputElement>(null);
  const [texto, setTexto] = useState("");
  const [marcado, setMarcado] = useState(0);

  useEffect(() => {
    if (enfocar > 0) casilla.current?.focus();
  }, [enfocar]);

  const todas = useMemo(() => {
    const lista: { enlace: Enlace; donde: string }[] = sueltos.map((e) => ({ enlace: e, donde: "" }));
    for (const g of grupos) {
      for (const e of g.enlaces) {
        lista.push({ enlace: e, donde: g.titulo });
        // Los mismos hijos que enseña el menú: ver `EnlaceLateral`.
        for (const h of e.hijos ?? []) {
          if (h.permiso && !puede(h.permiso)) continue;
          lista.push({ enlace: h, donde: `${g.titulo} › ${e.etiqueta}` });
        }
      }
    }
    return lista;
  }, [grupos, sueltos, puede]);

  const resultados = useMemo(() => {
    const buscado = sinTildes(texto.trim());
    if (!buscado) return [];
    const palabras = buscado.split(/\s+/);
    return todas
      .map((r) => {
        const nombre = sinTildes(r.enlace.etiqueta);
        const todo = `${nombre} ${sinTildes(r.enlace.claves ?? "")} ${sinTildes(r.donde)}`;
        if (!palabras.every((p) => todo.includes(p))) return null;
        // Primero lo que EMPIEZA por lo escrito, después lo que lo lleva en el
        // nombre, y al final lo que solo coincide por sus otras palabras.
        const orden = nombre.startsWith(buscado) ? 0 : nombre.includes(buscado) ? 1 : 2;
        return { ...r, orden };
      })
      .filter((r): r is NonNullable<typeof r> => r !== null)
      .sort((x, y) => x.orden - y.orden)
      .slice(0, 12);
  }, [texto, todas]);

  useEffect(() => setMarcado(0), [texto]);

  const terminar = () => {
    setTexto("");
    casilla.current?.blur();
  };

  if (oculto) return <div className={claseLista}>{children}</div>;

  return (
    <>
      <div className={claseCasilla}>
        <div className="relative">
          <Search
            size={15}
            className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-muted"
          />
          <input
            ref={casilla}
            type="search"
            value={texto}
            onChange={(e) => setTexto(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "ArrowDown") {
                e.preventDefault();
                setMarcado((m) => Math.min(m + 1, resultados.length - 1));
              } else if (e.key === "ArrowUp") {
                e.preventDefault();
                setMarcado((m) => Math.max(m - 1, 0));
              } else if (e.key === "Enter" && resultados[marcado]) {
                e.preventDefault();
                navegar(resultados[marcado].enlace.a);
                terminar();
              } else if (e.key === "Escape") {
                if (texto) setTexto("");
                else casilla.current?.blur();
              }
            }}
            placeholder="Buscar pantalla…"
            aria-label="Buscar una pantalla del menú"
            autoComplete="off"
            spellCheck={false}
            className={cx(
              "h-9 w-full rounded-[var(--radius-control)] border border-line bg-surface-2 pl-9 pr-9",
              "text-sm text-ink placeholder:text-muted outline-none transition-colors",
              "hover:border-line-strong focus:border-accent focus:ring-2 focus:ring-accent/20",
              "[&::-webkit-search-cancel-button]:hidden",
            )}
          />
          {texto ? (
            <button
              type="button"
              onClick={() => {
                setTexto("");
                casilla.current?.focus();
              }}
              aria-label="Borrar la búsqueda"
              className="absolute right-1.5 top-1/2 -translate-y-1/2 rounded-md p-1 text-muted transition-colors hover:bg-lateral-hover hover:text-ink"
            >
              <X size={14} />
            </button>
          ) : null}
        </div>
      </div>

      <div className={claseLista}>
        {!texto.trim() ? (
          children
        ) : resultados.length === 0 ? (
          <p className="px-3 py-6 text-center text-sm leading-relaxed text-muted">
            Ninguna pantalla coincide con «{texto.trim()}».
          </p>
        ) : (
          <div className="space-y-0.5" role="listbox" aria-label="Pantallas encontradas">
            {resultados.map((r, i) => {
              const Icono = r.enlace.icono;
              const activo = i === marcado;
              return (
                <Link
                  key={`${r.enlace.a}-${r.donde}`}
                  to={r.enlace.a}
                  role="option"
                  aria-selected={activo}
                  onClick={terminar}
                  onMouseEnter={() => setMarcado(i)}
                  className={cx(
                    "flex items-center gap-3 rounded-[var(--radius-control)] px-3 py-2 transition-colors",
                    activo ? "bg-lateral-activo text-lateral-activo-ink" : "text-ink-soft",
                  )}
                >
                  <Icono size={17} className={cx("shrink-0", activo ? "text-accent" : "text-muted")} />
                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-sm font-medium">{r.enlace.etiqueta}</span>
                    {r.donde && (
                      <span className="block truncate text-[11px] text-muted">{r.donde}</span>
                    )}
                  </span>
                  {activo && <CornerDownLeft size={13} className="hidden shrink-0 text-muted lg:block" />}
                </Link>
              );
            })}
          </div>
        )}
      </div>
    </>
  );
}
